import React, { useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Modal, Form, Input, Button, Select, Checkbox } from 'antd';
import {
  createAgentAction,
  listAgentsAction,
  listRetellAgentsAction,
} from '../../../../store/actions/agents.action';
import { showToastError } from 'src/utilities/errortoast';

const EHR_OPTIONS = [
  { label: 'Athena', value: 'athena' },
  { label: 'OpenDoctor', value: 'opendoctor' },
  { label: 'Charm', value: 'charm' },
  { label: 'AdvancedMD', value: 'advancedmd' },
];

const TEMPLATE_PREFIX = '[TEMPLATE]';

function parseTemplateName(agentName) {
  if (!agentName?.startsWith(TEMPLATE_PREFIX)) return null;
  const afterPrefix = agentName.slice(TEMPLATE_PREFIX.length);
  const ehrMatch = afterPrefix.match(/^\[([^\]]+)\]/);
  if (ehrMatch) {
    return {
      ehr: ehrMatch[1].toLowerCase(),
      displayName: afterPrefix.slice(ehrMatch[0].length).trim(),
    };
  }
  return { ehr: null, displayName: afterPrefix.trim() };
}

const AddNewAgent = ({ isOpen, setIsOpen, orgUuid, orgName }) => {
  const [form] = Form.useForm();
  const dispatch = useDispatch();
  const [creating, setCreating] = useState(false);
  const selectedEhr = Form.useWatch('ehr', form);

  const retellAgents = useSelector(
    (state) => state.agentsState.retellAgentsList
  );

  useEffect(() => {
    if (isOpen) {
      dispatch(listRetellAgentsAction());
      form.setFieldsValue({
        nickname: 'lumi',
        clinic_name: orgName || '',
      });
    }
  }, [dispatch, isOpen, form, orgName]);

  const templateAgents = useMemo(() => {
    const all = retellAgents.filter((a) =>
      a.agent_name?.startsWith(TEMPLATE_PREFIX)
    );
    if (!selectedEhr) return all;
    return all.filter((a) => {
      const parsed = parseTemplateName(a.agent_name);
      return parsed?.ehr === selectedEhr;
    });
  }, [retellAgents, selectedEhr]);

  const handleEhrChange = () => {
    form.setFieldsValue({ mirror_agent_id: undefined, name: undefined });
  };

  const handleTemplateSelect = (agentId) => {
    const agent = retellAgents.find((a) => a.agent_id === agentId);
    if (agent) {
      const parsed = parseTemplateName(agent.agent_name);
      const baseName = parsed?.displayName || agent.agent_name;
      form.setFieldsValue({
        name: `${orgName ? `${orgName} ` : ''}${baseName}`,
      });
    }
  };

  const handleClose = () => {
    form.resetFields();
    setCreating(false);
    setIsOpen(false);
  };

  const handleSubmit = (values) => {
    if (!orgUuid) {
      showToastError('Organization UUID is required');
      return;
    }

    const payload = {
      mirror_agent_id: values.mirror_agent_id,
      name: values.name.trim(),
      dynamic_variables: {
        nickname: values.nickname.trim(),
        clinic_name: values.clinic_name.trim(),
      },
      purchase_phone_number: !!values.purchase_phone_number,
    };

    setCreating(true);
    dispatch(
      createAgentAction(orgUuid, payload, () => {
        handleClose();
        dispatch(listAgentsAction(orgUuid));
      })
    ).finally(() => setCreating(false));
  };

  const modalTitle = orgName ? `Add Agent — ${orgName}` : 'Add Agent';

  return (
    <Modal
      title={modalTitle}
      open={isOpen}
      onCancel={handleClose}
      footer={null}
      width={700}
      destroyOnClose
    >
      <Form
        form={form}
        layout="horizontal"
        labelCol={{ span: 8 }}
        wrapperCol={{ span: 16 }}
        onFinish={handleSubmit}
        className="mt-4"
      >
        <Form.Item name="ehr" label="EHR">
          <Select
            placeholder="No EHR selected"
            allowClear
            options={EHR_OPTIONS}
            onChange={handleEhrChange}
          />
        </Form.Item>

        <Form.Item
          name="mirror_agent_id"
          label="Agent Template"
          rules={[
            { required: true, message: 'Please select an agent template' },
          ]}
        >
          <Select
            placeholder="Select an agent template"
            showSearch
            filterOption={(input, option) =>
              (option?.label ?? '').toLowerCase().includes(input.toLowerCase())
            }
            onChange={handleTemplateSelect}
            options={templateAgents.map((agent) => ({
              label: `${agent.agent_name}${agent.version ? ` (v${agent.version})` : ''}`,
              value: agent.agent_id,
            }))}
          />
        </Form.Item>

        <Form.Item
          name="name"
          label="Retell Agent Name"
          rules={[
            { required: true, message: 'Please enter retell agent name' },
            { min: 2, message: 'Name must be at least 2 characters' },
            { max: 100, message: 'Name must not exceed 100 characters' },
          ]}
        >
          <Input placeholder="Enter retell agent name" maxLength={100} />
        </Form.Item>

        <Form.Item
          name="nickname"
          label="Agent Nick Name"
          rules={[
            { required: true, message: 'Please enter agent nick name' },
            { max: 100, message: 'Nick name must not exceed 100 characters' },
          ]}
        >
          <Input placeholder="Enter agent nick name" maxLength={100} />
        </Form.Item>

        <Form.Item
          name="clinic_name"
          label="Clinic Name"
          rules={[
            { required: true, message: 'Please enter clinic name' },
            {
              max: 200,
              message: 'Clinic name must not exceed 200 characters',
            },
          ]}
        >
          <Input placeholder="Enter clinic name" maxLength={200} />
        </Form.Item>

        <Form.Item
          name="purchase_phone_number"
          valuePropName="checked"
          wrapperCol={{ offset: 8, span: 16 }}
        >
          <Checkbox>Purchase Phone Number</Checkbox>
        </Form.Item>

        <Form.Item wrapperCol={{ offset: 8, span: 16 }}>
          <div className="flex justify-end gap-2">
            <Button onClick={handleClose}>Cancel</Button>
            <Button
              type="primary"
              htmlType="submit"
              disabled={!orgUuid}
              loading={creating}
            >
              Create Agent
            </Button>
          </div>
        </Form.Item>
      </Form>
    </Modal>
  );
};

export default AddNewAgent;
